import SearchContainer from '../../features/search/index';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSidePropsContext } from 'next';
import {
  getAreasCached,
  getCompoundsCached,
  getFilterOptions,
  getFooterLinks,
  getStatistics
} from '../../new-lib/apis';
import {
  convertSlugIntoFilters,
  handlePrepareFiltersOptions,
  handleSeoHeader,
  loadSearchResults
} from '../../helpers/buy';
import { getSelectorsByUserAgent } from 'react-device-detect';
import {
  setIsMobile,
  setExperimentVariant
} from '../../redux/actions/appActions';
import { wrapper } from '../../redux/store';

const Buy = (props: any) => {
  return <SearchContainer {...props} />;
};

export default Buy;

export const getServerSideProps = wrapper.getServerSideProps(
  store =>
    async ({ req, query, locale }: GetServerSidePropsContext) => {
      try {
        const [footerLinks, areas, compounds, otherFilters, filterOptions] =
          await Promise.all([
            getFooterLinks(locale, 'buy'),
            getAreasCached(locale),
            getCompoundsCached(locale),
            getStatistics(locale),
            getFilterOptions(locale)
          ]);
        const allFinishing = otherFilters.finishings.map((curr: any) => [
          curr,
          curr.replace('-', '_')
        ]);
        const filtersOptions = {
          property_type: otherFilters.property_types,
          area: areas,
          compounds: compounds,
          finishing: allFinishing,
          bedrooms: otherFilters.bedrooms
        };

        const preparedFiltersOptions =
          handlePrepareFiltersOptions(filtersOptions);
        const newQuery = convertSlugIntoFilters(
          query.buy_slug as string,
          preparedFiltersOptions
        );

        const loadedSearchResults = await loadSearchResults(newQuery, locale);

        const isMobile = getSelectorsByUserAgent(
          req.headers['user-agent']!
        ).isMobile;

        store.dispatch(setIsMobile(isMobile));
        store.dispatch(setExperimentVariant(req.cookies['_ab'] ?? null));

        const propertyTypeMap = new Map([
          [21, 'Apartments'],
          [1, 'Villas'],
          [4, 'Penthouses'],
          [7, 'Townhouses'],
          [6, 'Twinhouses']
        ]);

        const propertyTypes = [
          propertyTypeMap.get(newQuery.property_types) || 'Properties'
        ];

        const getMetaTitle = () => {
          if (
            !loadedSearchResults?.values ||
            loadedSearchResults.values.length === 0
          ) {
            return locale === 'en'
              ? 'Find the top properties for sale with the best prices!'
              : 'ابحث عن أفضل العقارات للبيع بأفضل الأسعار';
          }

          const { compound, developer, area } = loadedSearchResults.values[0];
          const compoundName = compound?.name || '';
          const developerName = developer?.name || '';
          const areaName = area?.name || '';

          return locale === 'en'
            ? `Find the top ${propertyTypes} for sale in ${compoundName} from ${developerName} with the best prices!`
            : `عقارات للبيع في ${compoundName} ${areaName} بأفضل الأسعار`;
        };

        const getMetaDescription = () => {
          if (
            !loadedSearchResults?.values ||
            loadedSearchResults.values.length === 0
          ) {
            return locale === 'en'
              ? 'Explore the best properties for sale with Nawy.'
              : 'استكشف أفضل العقارات للبيع مع ناوي.';
          }

          const { compound, area } = loadedSearchResults.values[0];
          const compoundName = compound?.name || '';
          const areaName = area?.name || '';

          return locale === 'en'
            ? `Apartments for sale in ${compoundName} - Villas for sale in ${compoundName} - Properties for sale in ${compoundName} - ${compoundName} prices - Units for sale in ${compoundName} - properties for sale in ${areaName}.`
            : `عقارات للبيع في ${compoundName} بأفضل سعر، تعرف على شقق للبيع في ${compoundName} بـ${areaName}، أسعار ${compoundName}، شقق للبيع بمساحات مختلفة في كمبوند ${compoundName} شقق تمليك بكمبوند( ${compoundName} - ${areaName}) ، شقق بالتقسيط في كمبوند ${compoundName}.`;
        };

        return {
          props: {
            ...(await serverSideTranslations(locale as string, [
              'common',
              'search'
            ])),
            query: { ...newQuery, category: 'property' },
            loadedSearchResultsSSR: loadedSearchResults,
            filterOptions: JSON.stringify(filterOptions) || '',
            pageTitle: handleSeoHeader(query.buy_slug as string),
            layout: {
              title: getMetaTitle(),
              url: `${process.env.NEXT_PUBLIC_HOST}/search`,
              footerLinks,
              isMobile,
              metaDescription: getMetaDescription()
            }
          }
        };
      } catch (err) {
        return {
          notFound: true
        };
      }
    }
);
